import { ChimeChannelMessageTypes } from '@eigtech/meetings-types'
import { useCohortContext } from '@eigtech/ui-shared-api'
import { FileUpload } from '@eigtech/ui-shared-files'
import { getLog } from '@eigtech/ui-shared-logging'
import { toast } from '@eigtech/ui/components/sonner'
import { useAudioVideo } from 'amazon-chime-sdk-component-library-react'
import { pick } from 'lodash-es'
import { useCallback, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/shallow'
import { useGetSdkToken } from '../api/getSdkToken'
import { useUploadScreenshot } from '../api/uploadScreenshot'
import { sendChannelMessage } from '../chime-sdk/chimeApi'
import { MeetingState, useMeetingStore } from '../context/MeetingState'
import { getFileFromCanvas } from '../utils/getFileFromCanvas'

const log = getLog()

export type UseCaptureImageFromVideoProps = {
  tileId: number | undefined
  getMeta?: (image: File) => Promise<Record<string, string> | undefined>
}

export function useCaptureImageFromVideo({ tileId, getMeta }: UseCaptureImageFromVideoProps) {
  const audioVideo = useAudioVideo()

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const downloadRef = useRef<HTMLAnchorElement>(null)

  const { cohort } = useCohortContext()
  const { canScreenshot, meetingDetails, participant } = useMeetingStore(useShallow(stateSelector))

  if (!meetingDetails) {
    throw new Error('meeting details missing')
  }

  const { data: credentials } = useGetSdkToken({
    entityId: meetingDetails.entityId,
    startTime: meetingDetails.startTime,
  })

  const { mutateAsync: uploadScreenshot } = useUploadScreenshot({
    cohort,
    entityId: meetingDetails.entityId,
    meetingId: meetingDetails.meetingId,
  })

  useEffect(() => {
    if (!canvasRef.current) {
      log.warn('Make sure to set canvasRef in order for useCaptureImageFromVideo hook to work')
    }
    if (!downloadRef.current) {
      log.warn('Make sure to set downloadRef in order for useCaptureImageFromVideo hook to work')
    }
  }, [])

  const capturePhoto = useCallback(async () => {
    if (!canScreenshot) return

    if (meetingDetails.status !== 'closed' && credentials && participant) {
      // Intentionally not awaiting (don't slow anything down here)
      sendChannelMessage(
        credentials,
        meetingDetails.appInstanceArn!,
        meetingDetails.chimeChannelArn!,
        participant.auth0Id ?? participant.attendeeId ?? 'guest',
        participant.name,
        ChimeChannelMessageTypes.markScreenshot
      )
    } else {
      log.warn('Missing some parameters to send screenshot message', {
        status: meetingDetails.status,
        credentials,
        participant,
      })
    }

    if (!(canvasRef.current && downloadRef.current)) {
      throw new Error(
        'Attempting to use useCaptureImageFromVideo without adding canvas & download refs'
      )
    }

    if (!audioVideo) {
      throw new Error('useCaptureImageFromVideo needs AudioVideo provider to work')
    }

    if (tileId === undefined) {
      throw new Error('tileId required to capture photo')
    }

    const image = audioVideo.captureVideoTile(tileId)
    if (!image) {
      toast.warning('Something went wrong trying to capture photo from video.')
      return
    }

    canvasRef.current.width = image.width
    canvasRef.current.height = image.height

    const canvasContext = canvasRef.current.getContext('2d')
    if (!canvasContext) {
      toast.warning('Something went wrong trying to capture photo from video.')
      return
    }

    canvasContext.clearRect(0, 0, image.width, image.height)
    canvasContext.putImageData(image, 0, 0)

    const file = await getFileFromCanvas({ canvasRef, meetingId: meetingDetails.meetingId })

    const payload: FileUpload = { file, 'Content-Type': 'image/png' }

    if (getMeta) {
      payload.meta = await getMeta(file)
    }

    uploadScreenshot(payload)
  }, [
    audioVideo,
    canScreenshot,
    credentials,
    getMeta,
    meetingDetails.meetingId,
    meetingDetails.appInstanceArn,
    meetingDetails.chimeChannelArn,
    meetingDetails.status,
    participant,
    tileId,
    uploadScreenshot,
  ])

  return { canvasRef, downloadRef, capturePhoto }
}

const stateSelector = (state: MeetingState) =>
  pick(state, ['canScreenshot', 'cohort', 'meetingDetails', 'participant'])
